import React, { useEffect, useCallback, useState } from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { AddFarmIcon, DelIcon, MetrcLogo, PlusIcon} from '../utility/CustomSvgIcons';
import { InfoIcon } from '../utility/CustomSvgIcons';
import { compClasses } from '../utility/constants';
import { ListItemText, MenuItem  } from '@mui/material';

import { phasePlanClasses} from '../utility/constants';

export default function SubPhaseEntry(props) {
  const { currDarkThemeClass = '', pos, subphase = {}, subPhaseErr = false, values, handleSetValues} = props;

  const getSelectedRoutines = () => {
  if (!subphase?.default_routines) return [];
  
  // Handle both array of IDs and array of objects
  if (Array.isArray(subphase.default_routines)) {
    if (subphase.default_routines.length === 0) return [];
    
    // If it's array of objects, extract IDs
    if (typeof subphase.default_routines[0] === 'object' && subphase.default_routines[0]?.id) {
      return subphase.default_routines.map(routine => routine.id);
    }
    
    // If it's array of IDs, return as is, but ensure they're numbers
    if (typeof subphase.default_routines[0] === 'number' || typeof subphase.default_routines[0] === 'string') {
      return subphase.default_routines.map(id => Number(id)).filter(id => !isNaN(id));
    }
  }
  
  return [];
};

  // In SubPhaseEntry.js - Fix the useEffect that converts complied_routine_idx to routine IDs

useEffect(() => {
  if (!subphase) return;

  if (subphase.isIncluded === 'undefined') {
    handleSetSubPhase('isIncluded', true)({ target: { checked: true } });
  }
  
  if (subphase?.id || subphase?.complied_routine_idx || subphase?.default_routines) {
    // Handle multiple cases: existing default_routines, complied_routine_idx from API, or routine objects
    let defaultRoutineIds = [];
    
    if (subphase?.default_routines && subphase.default_routines.length > 0) {
      // Check if default_routines contains objects or IDs
      if (typeof subphase.default_routines[0] === 'object' && subphase.default_routines[0]?.id) {
        // Case 1: default_routines contains full routine objects (after save)
        defaultRoutineIds = subphase.default_routines.map(routine => routine.id);
      } else if (typeof subphase.default_routines[0] === 'number') {
        // Case 2: default_routines contains routine IDs (before save)
        defaultRoutineIds = subphase.default_routines;
      }
    } else if (subphase?.complied_routine_idx && subphase.complied_routine_idx.length > 0) {
      // Case 3: Has complied_routine_idx (indices) - convert to routine IDs
      // The complied_routine_idx contains indices that correspond to ALL routines array
      const allRoutines = values?.default_routines || [];
      
      // FIXED: Make sure we're filtering out undefined values and only getting valid routine IDs
      defaultRoutineIds = subphase.complied_routine_idx
        .map(index => {
          const routine = allRoutines[index];
          return routine?.id;
        })
        .filter(id => id !== undefined && id !== null);
    }
    
    // Update the subphase with routine IDs if we have any valid IDs
    if (defaultRoutineIds.length > 0) {
      handleSetSubPhase('default_routines')({ 
        target: { value: defaultRoutineIds } 
      });
    }
    
    // IMPORTANT: Clear complied_routine_idx after conversion to avoid confusion
    if (subphase?.complied_routine_idx && defaultRoutineIds.length > 0) {
      // You might want to clear this field to avoid re-processing
      const updatedPhases = [...values?.subphases];
      const currentIndex = updatedPhases.findIndex(phase => phase.id === subphase.id);
      if (currentIndex !== -1) {
        updatedPhases[currentIndex] = {
          ...updatedPhases[currentIndex],
          default_routines: defaultRoutineIds,
          complied_routine_idx: undefined // Clear this to avoid re-processing
        };
        handleSetValues('subphases', updatedPhases);
      }
    }
  }
}, [subphase?.id, subphase?.complied_routine_idx, subphase?.default_routines, values?.default_routines]);
  useEffect(() => {
    const updatedPhases = [...values?.subphases || []];
    const currentSubphase = updatedPhases[pos];
  
    if (currentSubphase) {
      const startDay = Number(currentSubphase.start_day) || 1;
      const endDay = Number(currentSubphase.end_day) || startDay;
      const calculatedTotal = Math.max(0, endDay - startDay + 1);
  
      if (currentSubphase.total_day !== calculatedTotal) {
        updatedPhases[pos] = {
          ...currentSubphase,
          total_day: calculatedTotal,
        };
        handleSetValues('subphases', updatedPhases);
      }
    }
  }, [values?.subphases, pos, handleSetValues]);
  
  const subPhaseEntryBoxProps = {
    className: `${compClasses?.subPhaseEntry?.box} ${currDarkThemeClass}`,
  };

  const subPhaseEntryErrorTitle = 'Invalid value';

  const subPhaseEntryTextInfoIconProps = {
    className: `${compClasses?.subPhaseEntry?.textInfoIcon} ${currDarkThemeClass}`,
  };

  const subPhaseEntryTextFieldProps = ({ prop, inputProps = {}, otherSxProps = {}, otherInputProps = {}, ...otherTextProps }) => ({
    autoComplete: 'off',
    className: `${compClasses?.subPhaseEntry?.textField} ${currDarkThemeClass} ${
      otherTextProps?.disabled ? compClasses?.subPhaseEntry?.disabled : ''
    }`,
    disabled: values?.loading?.processing || values?.loading?.fetching,
    inputProps,
    InputProps: {
      endAdornment: subPhaseErr && prop !== 'name' && subphase?.isIncluded && (
        <Tooltip title={subPhaseEntryErrorTitle}>
          <Box>
            <InfoIcon {...subPhaseEntryTextInfoIconProps} />
          </Box>
        </Tooltip>
      ),
      ...otherInputProps,
    },
    label: ' ',
    required: true,
    sx: {
      ...otherSxProps,
    },
    ...(prop && {
      onChange: handleSetSubPhase(prop),
      value: subphase?.[prop] ?? '',
    }),
    ...otherTextProps,
  });

  const handleAddSubPhaseEntry = useCallback((values, handleSetValues, index) => () => {
    const id = `tempRef-${values?.subphases?.length || 0}`;
    const newSubPhases = { 
      name: '', 
      id, 
      default_routines: [], 
      isIncluded: true, 
      is_metrc_compliance: false,
      priority: index + 1,
    };

    let updatedSubPhases = [...values?.subphases];
    updatedSubPhases.splice(index + 1, 0, newSubPhases);
    
    updatedSubPhases = updatedSubPhases.map((subphase, i) => ({
      ...subphase,
      priority: i
    }));
    
    handleSetValues('subphases', updatedSubPhases);
  }, [values?.subphases, handleSetValues]);

  const phasePlanAddSubPhaseIconProps = {
    className: `${phasePlanClasses?.addSubPhaseIcon} ${currDarkThemeClass}`,
  };
  
  const phasePlanAddSubPhaseBtnProps = {
    children: 'Add Sub Phase',
    className: `${phasePlanClasses?.addSubPhaseBtn} ${currDarkThemeClass}`,
    disabled: values?.loading?.processing || values?.loading?.fetching,
    onClick: handleAddSubPhaseEntry(values, handleSetValues, pos),
    startIcon: <AddFarmIcon {...phasePlanAddSubPhaseIconProps} />,
  };

  const handleSetSubPhase = useCallback(
    (prop, check = false) =>
      ({ target: { value, checked, valueAsNumber } }) => {
        if ((prop === 'start_day' || prop === 'end_day' || prop === 'total_day') && valueAsNumber < 1) return;
        
        let updatedPhases = [...values?.subphases];
        const currentIndex = pos;

        // Update the current subphase
        updatedPhases[currentIndex] = {
          ...updatedPhases[currentIndex],
          [prop]: check ? checked : value,
        };

        // Handle day calculations (existing logic)
        if (prop === 'start_day') {
          const newStartDay = Number(value);
          const totalDays = Number(updatedPhases[currentIndex]?.total_day) || 1;
          const newEndDay = newStartDay + totalDays - 1;

          updatedPhases[currentIndex].end_day = newEndDay;
          
          if (currentIndex > 0) {
            updatedPhases[currentIndex - 1].end_day = newStartDay;
          }

          let previousEndDay = newEndDay;
          for (let i = currentIndex + 1; i < updatedPhases.length; i++) {
            const nextStartDay = previousEndDay;
            const totalDaysNext = updatedPhases[i].total_day || 1;
            const nextEndDay = nextStartDay + totalDaysNext - 1;
  
            updatedPhases[i] = {
              ...updatedPhases[i],
              start_day: nextStartDay,
              end_day: nextEndDay,
            };
            previousEndDay = nextEndDay;
          }
        }
        
        if (prop === 'total_day') {
          const newTotalDays = Number(value);
          const startDay = Number(updatedPhases[currentIndex]?.start_day) || 1;
          const newEndDay = startDay + Number(newTotalDays) - 1;
          updatedPhases[currentIndex].end_day = newEndDay;

          let previousEndDay = newEndDay;
          for (let i = currentIndex + 1; i < updatedPhases.length; i++) {
            const nextStartDay = previousEndDay;
            const totalDaysNext = updatedPhases[i].total_day || 1;
            const nextEndDay = nextStartDay + totalDaysNext - 1;

            updatedPhases[i] = {
              ...updatedPhases[i],
              start_day: nextStartDay,
              end_day: nextEndDay,
            };
            previousEndDay = nextEndDay;
          }
        }
        
        if (prop === 'end_day') {
          const newEndDay = Number(value);
          const startDay = Number(updatedPhases[currentIndex]?.start_day) || 1;
          const newTotalDays = newEndDay - startDay + 1;
          updatedPhases[currentIndex].total_day = newTotalDays;

          let previousEndDay = newEndDay;
          for (let i = currentIndex + 1; i < updatedPhases.length; i++) {
            const nextStartDay = previousEndDay;
            const totalDaysNext = updatedPhases[i].total_day || 1;
            const nextEndDay = nextStartDay + totalDaysNext - 1;

            updatedPhases[i] = {
              ...updatedPhases[i],
              start_day: nextStartDay,
              end_day: nextEndDay,
            };
            previousEndDay = nextEndDay;
          }
        }

        handleSetValues('subphases', updatedPhases);
      },
    [values?.subphases, subphase?.id, pos, handleSetValues]
  );

  const handleDeleteSubPhase = useCallback(() => {
    let updatedPhases = values?.subphases.filter((_, index) => index !== pos);
    updatedPhases = updatedPhases.map((subphase, index) => ({
      ...subphase,
      priority: index
    }));
    handleSetValues('subphases', updatedPhases);
  }, [values?.subphases, pos, handleSetValues]);

  const startDayProps = {
    autoFocus: pos === 0,
    inputProps: {
      min: 1,
    },
    label: 'Start Day',
    otherSxProps: {
      flexShrink: 1,
      ml: '16px',
    },
    prop: 'start_day',
    required: subphase?.isIncluded ?? true,
    type: 'number',
  };

  const endDayProps = {
    inputProps: {
      min: 1,
    },
    label: 'End Day',
    otherSxProps: {
      flexShrink: 1,
      ml: '16px',
    },
    prop: 'end_day',
    required: subphase?.isIncluded ?? true,
    type: 'number',
  };
  
  const getTotalDaysProps = (subphase) => ({
    disabled: false,
    label: 'Total Days',
    otherSxProps: {
      flexShrink: 1,
      ml: '16px',
    },
    prop: 'total_day',
    required: subphase?.isIncluded ?? true,
    type: 'number',
    value: (+subphase?.end_day - +subphase?.start_day + 1) || '',
  });
  
  const nameProps = {
    disabled: subphase?.is_metrc_compliance ? true : false,
    label: 'Name',
    inputProps: {
      maxLength: 100,
    },
    otherSxProps: {
      flexShrink: 1,
      ml: '16px',
    },
    prop: 'name',
    required: subphase?.is_metrc_compliance ?? true,
  };

  // Extract all routines
  const allRoutines = values?.default_routines ?? [];

  // Separate compiled and non-compiled routines
  const compiledRoutines = allRoutines.filter(e => e?.is_metrc_compliance === true);
  const nonCompiledRoutines = allRoutines.filter(e => e?.is_metrc_compliance === false);

  // Get selected routines for this specific subphase
  const selectedRoutines = getSelectedRoutines();

  // Fixed routine change handler
  const handleRoutineChange = (routineId) => {
    const currentSelected = getSelectedRoutines();
    const newSelected = currentSelected.includes(routineId)
      ? currentSelected.filter((id) => id !== routineId)
      : [...currentSelected, routineId];
    
    // Update the subphase data directly
    handleSetSubPhase('default_routines')({ target: { value: newSelected } });
  };

  const renderRoutinesList = (routineList) =>
    routineList.map((e, i) => (
      <MenuItem 
        key={`routine-${e?.id || i}`} 
        value={e?.id}
        onClick={() => handleRoutineChange(e?.id)}
      >
        <Checkbox
          checked={selectedRoutines.includes(e?.id)}
          onChange={() => {}} // Prevent default behavior
        />
        <ListItemText primary={e?.name} />
      </MenuItem>
    ));

  // Create dropdown lists
  let compiledRoutinesList = renderRoutinesList(compiledRoutines);
  let nonCompiledRoutinesList = renderRoutinesList(nonCompiledRoutines);

  // Handle empty dropdown cases
  if (!compiledRoutinesList.length) {
    compiledRoutinesList = [
      <MenuItem key="no-compiled" value="" disabled>
        No Available Compiled Routines
      </MenuItem>
    ];
  }

  if (!nonCompiledRoutinesList.length) {
    nonCompiledRoutinesList = [
      <MenuItem key="no-non-compiled" value="" disabled>
        No Available Non-Compiled Routines
      </MenuItem>
    ];
  }
  const compliedRoutinesProps = {
  disabled: false,
  label: 'Complied Routines',
  select: true,
  SelectProps: {
    renderValue: (selected) => {
      const currentSelectedRoutines = getSelectedRoutines();
      console.log('Rendering complied routines, selected:', currentSelectedRoutines); // Debug log
      
      const selectedNames = allRoutines
        .filter(e => currentSelectedRoutines.includes(Number(e?.id)) && e?.is_metrc_compliance === true)
        .map(e => e?.name)
        .join(', ');
      return selectedNames || 'Complied Routines';
    },
    multiple: true,
  },
  otherSxProps: { flexShrink: 1, ml: '16px' },
  required: false,
  value: getSelectedRoutines(), // Use the function directly here
  prop: 'default_routines',
};

const nonCompliedRoutinesProps = {
  disabled: false,
  label: 'Non Complied Routines',
  select: true,
  SelectProps: {
    renderValue: (selected) => {
      const currentSelectedRoutines = getSelectedRoutines();
      console.log('Rendering non-complied routines, selected:', currentSelectedRoutines); // Debug log
      
      const selectedNames = allRoutines
        .filter(e => currentSelectedRoutines.includes(Number(e?.id)) && e?.is_metrc_compliance === false)
        .map(e => e?.name)
        .join(', ');
      return selectedNames || 'Non Complied Routines';
    },
    multiple: true,
  },
  otherSxProps: { flexShrink: 1, ml: '16px' },
  required: false,
  value: getSelectedRoutines(), // Use the function directly here
  prop: 'default_routines',
};


  const includeSubPhaseMsg = !subphase?.isIncluded ? 'Enable checkbox to add values' : '';

  return (
    <Box {...subPhaseEntryBoxProps}>
      <Box sx={{ display: 'flex', alignItems: 'center', ml: '40px' }}>
        {subphase.is_metrc_compliance && (
          <MetrcLogo sx={{ ml: -2}} /> 
        )}
      </Box>
      <Tooltip title={includeSubPhaseMsg}>
        <TextField 
          {...subPhaseEntryTextFieldProps(nameProps)}  
          onChange={handleSetSubPhase('name')}  
          sx={{ width: '20%' }} 
          required
          error={!nameProps?.value}
        />
      </Tooltip>
      <Tooltip title={includeSubPhaseMsg}>
        <TextField {...subPhaseEntryTextFieldProps(startDayProps)} sx={{ width: '10%' }} />
      </Tooltip>
      <Tooltip title={includeSubPhaseMsg} >
        <TextField {...subPhaseEntryTextFieldProps(getTotalDaysProps(subphase))} sx={{ width: '10%' }}/>
      </Tooltip>
      <Tooltip title={includeSubPhaseMsg}>
        <TextField {...subPhaseEntryTextFieldProps(endDayProps)} sx={{ width: '10%' }} />
      </Tooltip>
      <Tooltip title={includeSubPhaseMsg}>
        <TextField {...subPhaseEntryTextFieldProps(compliedRoutinesProps)} sx={{ width: '15%' }}>
          {compiledRoutinesList}
        </TextField>
      </Tooltip>
      <Tooltip title={includeSubPhaseMsg}>
        <TextField {...subPhaseEntryTextFieldProps(nonCompliedRoutinesProps)} sx={{ width: '15%' }}>
          {nonCompiledRoutinesList}
        </TextField>
      </Tooltip>
      <Tooltip title="Add Sub Phase Below">
        <Box sx={{ display: 'flex', alignItems: 'center', ml: '10px' }}>
          <PlusIcon sx={{ ml: -1}} {...phasePlanAddSubPhaseBtnProps}/>
        </Box>
      </Tooltip>
      <Tooltip title="Delete Sub Phase">
        <Box sx={{ display: 'flex', alignItems: 'center', ml: '10px' }}>
          {!subphase.is_metrc_compliance && <DelIcon onClick={handleDeleteSubPhase} sx={{ mr: 1}}/>}
        </Box>
      </Tooltip>
    </Box>
  );
}